import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  SendIcon,
  TrophyIcon,
  XCircleIcon,
} from 'lucide-react';
import type { ExamQuestionClient } from '@kennisbank/shared';
import { ApiError, apiPost } from '../../api/client.js';
import { CertificaatKnop } from './CertificaatKnop.js';
import { ELEARNING_BASE } from '../../pages/ELearnings.js';
import type { StartExamResponse, SubmitExamResponse } from '../../pages/ELearnings.js';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

type Phase = 'starting' | 'start_failed' | 'taking' | 'submitting' | 'result';

// Vertaal de contract-foutcodes naar begrijpelijke meldingen (de reden dat het
// examen niet kan starten). `cooldown` draagt detail.cooldownUntil.
function startErrorMessage(err: unknown): { message: string; cooldownUntil: string | null } {
  if (err instanceof ApiError) {
    const detail = err.detail as { cooldownUntil?: string } | undefined;
    switch (err.code) {
      case 'exam_not_ready':
        return {
          message: 'Rond eerst alle lessen af voordat je het examen start.',
          cooldownUntil: null,
        };
      case 'already_passed':
        return { message: 'Je bent al geslaagd voor dit examen.', cooldownUntil: null };
      case 'no_retakes_left':
        return {
          message: 'Je hebt geen herkansingen meer. Neem contact op met SUPPOHRT.',
          cooldownUntil: null,
        };
      case 'cooldown':
        return {
          message: 'Er geldt een wachttijd na je vorige poging.',
          cooldownUntil: detail?.cooldownUntil ?? null,
        };
      default:
        return { message: 'Het examen kon niet worden gestart.', cooldownUntil: null };
    }
  }
  return { message: 'Het examen kon niet worden gestart.', cooldownUntil: null };
}

function minutesUntil(iso: string | null): number {
  if (!iso) return 0;
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / 60000));
}

function sortedOptions(question: ExamQuestionClient): { text: string; sortOrder: number }[] {
  return [...question.answerOptions].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function StudentExam({
  courseId,
  onBack,
}: {
  courseId: string;
  onBack: () => void;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('starting');
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ExamQuestionClient[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [result, setResult] = useState<SubmitExamResponse | null>(null);
  const [startError, setStartError] = useState<{ message: string; cooldownUntil: string | null }>({
    message: '',
    cooldownUntil: null,
  });
  const [showConfirm, setShowConfirm] = useState(false);

  // Examen starten bij binnenkomst (server stelt samen zonder juiste antwoorden).
  useEffect(() => {
    let cancelled = false;
    setPhase('starting');
    void apiPost<StartExamResponse>(`${ELEARNING_BASE}/courses/${courseId}/exam`)
      .then((data) => {
        if (cancelled) return;
        setAttemptId(data.attemptId);
        setQuestions(data.questions);
        setPhase('taking');
      })
      .catch((err) => {
        if (cancelled) return;
        setStartError(startErrorMessage(err));
        setPhase('start_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  // Waarschuw bij wegnavigeren tijdens het examen.
  useEffect(() => {
    if (phase !== 'taking') return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  const currentQuestion = currentIndex < questions.length ? questions[currentIndex] : null;
  const answeredCount = Object.keys(answers).length;
  const unansweredCount = questions.length - answeredCount;

  const options = useMemo(
    () => (currentQuestion ? sortedOptions(currentQuestion) : []),
    [currentQuestion],
  );
  const selectedForCurrent = currentQuestion ? (answers[currentQuestion.id]?.[0] ?? null) : null;

  const handleSelect = useCallback((questionId: string, optionIndex: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [optionIndex] }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!attemptId) return;
    setShowConfirm(false);
    setPhase('submitting');
    const payload = {
      answers: Object.entries(answers).map(([questionId, selectedOptionIndexes]) => ({
        questionId,
        selectedOptionIndexes,
      })),
    };
    void apiPost<SubmitExamResponse>(
      `${ELEARNING_BASE}/courses/${courseId}/exam/${attemptId}/submit`,
      payload,
    )
      .then((res) => {
        setResult(res);
        setPhase('result');
      })
      .catch(() => setPhase('taking'));
  }, [attemptId, answers, courseId]);

  // ─── Starten / startfout ───────────────────────────────────────────────────

  if (phase === 'starting') {
    return (
      <div className="py-20 text-center text-[13px] text-ink-900/50">Examen wordt voorbereid…</div>
    );
  }

  if (phase === 'start_failed') {
    const cooldownMin = minutesUntil(startError.cooldownUntil);
    return (
      <div className="mx-auto max-w-md px-5 py-12">
        <div className="rounded-2xl border border-brand-100 bg-white p-8 text-center shadow-sm">
          <AlertTriangleIcon size={40} className="mx-auto mb-4 text-error" />
          <h2 className="text-[17px] font-bold text-ink-900">Examen kan niet starten</h2>
          <p className="mt-2 text-[13px] text-ink-900/65">{startError.message}</p>
          {cooldownMin > 0 && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-mosterdgeel-10 px-3 py-1.5 text-[13px] font-medium text-mosterdgeel-600">
              <ClockIcon size={14} />
              Probeer het over {cooldownMin} minuut{cooldownMin !== 1 ? 'en' : ''} opnieuw.
            </p>
          )}
          <div className="mt-6">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 rounded-xl border border-brand-200 bg-white px-4 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
            >
              <ArrowLeftIcon size={14} />
              Terug naar de cursus
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'submitting') {
    return (
      <div className="py-20 text-center text-[13px] text-ink-900/50">Examen wordt nagekeken…</div>
    );
  }

  // ─── Uitslag ─────────────────────────────────────────────────────────────────

  if (phase === 'result' && result) {
    const cooldownMin = minutesUntil(result.cooldownUntil);
    return (
      <div className="mx-auto max-w-2xl px-5 py-8">
        {result.passed ? (
          <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
            <TrophyIcon size={56} className="mx-auto mb-4 text-green-600" />
            <h2 className="text-[24px] font-bold text-ink-900">Geslaagd!</h2>
            <p className="mt-1 text-[14px] text-ink-900/65">
              Gefeliciteerd, je bent geslaagd voor het examen.
            </p>
            <div className="mt-4 text-[36px] font-bold text-green-600">
              {result.scorePercentage}%
            </div>
            <div className="flex justify-center">
              <CertificaatKnop courseId={courseId} />
            </div>
          </div>
        ) : (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <XCircleIcon size={56} className="mx-auto mb-4 text-red-600" />
            <h2 className="text-[24px] font-bold text-ink-900">Helaas niet geslaagd</h2>
            <p className="mt-1 text-[14px] text-ink-900/65">
              Je hebt de minimale score niet gehaald.
            </p>
            <div className="mt-4 text-[36px] font-bold text-red-600">{result.scorePercentage}%</div>
          </div>
        )}

        {/* Je score naast het cursusgemiddelde (Hupper-patroon) */}
        {result.courseAverageScore != null && (
          <div className="mb-6 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-[14px] font-bold text-ink-900">Vergelijking</h3>
            <div className="space-y-3">
              <ScoreBar
                label="Gemiddelde score"
                value={result.courseAverageScore}
                barClass="bg-ink-700"
              />
              <ScoreBar
                label="Je score"
                value={result.scorePercentage}
                barClass="bg-mosterdgeel-500"
              />
            </div>
          </div>
        )}

        {/* Score per moduul */}
        {result.scorePerModule.length > 0 && (
          <div className="mb-6 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-[14px] font-bold text-ink-900">Score per onderdeel</h3>
            <div className="space-y-4">
              {result.scorePerModule.map((mod) => (
                <div key={mod.moduleId}>
                  <div className="mb-1 flex items-center justify-between text-[13px]">
                    <span className="font-medium text-ink-900">{mod.moduleName}</span>
                    <span className="text-ink-900/55">
                      {mod.correct} / {mod.total} ({mod.percentage}%)
                    </span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-brand-100">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        mod.percentage >= 60 ? 'bg-green-500' : 'bg-mosterdgeel-500'
                      }`}
                      style={{ width: `${mod.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Herkansing / einde */}
        {!result.passed && (
          <div className="mb-6 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
            {result.attemptsRemaining > 0 ? (
              <div className="flex items-start gap-3">
                <ClockIcon size={20} className="mt-0.5 shrink-0 text-ink-900/50" />
                <div>
                  <p className="text-[13px] font-medium text-ink-900">
                    Je hebt nog {result.attemptsRemaining} poging
                    {result.attemptsRemaining !== 1 ? 'en' : ''} over.
                  </p>
                  {cooldownMin > 0 && (
                    <p className="mt-1 text-[13px] text-ink-900/60">
                      Je kunt het over {cooldownMin} minuut{cooldownMin !== 1 ? 'en' : ''} opnieuw
                      proberen.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertTriangleIcon size={20} className="mt-0.5 shrink-0 text-error" />
                <div>
                  <p className="text-[13px] font-medium text-ink-900">
                    Maximaal aantal pogingen bereikt
                  </p>
                  <p className="mt-1 text-[13px] text-ink-900/60">
                    Neem contact op met SUPPOHRT voor een herkansing.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <ArrowLeftIcon size={16} />
            Terug naar de cursus
          </button>
        </div>
      </div>
    );
  }

  // ─── Examen afnemen ──────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-2xl px-5 py-8">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[13px] font-medium text-ink-900">
          Vraag {currentIndex + 1} van {questions.length}
        </span>
        <span className="rounded-md border border-brand-100 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700">
          {answeredCount} / {questions.length} beantwoord
        </span>
      </div>

      <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-brand-100">
        <div
          className="h-full rounded-full bg-brand-500 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>

      {currentQuestion && (
        <div className="mb-6 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold leading-relaxed text-ink-900">
            {currentQuestion.title}
          </h2>
          {currentQuestion.questionText && (
            <p className="mt-2 text-[13px] text-ink-900/65">{currentQuestion.questionText}</p>
          )}

          <div className="mt-4 space-y-3">
            {options.map((option, index) => {
              const isSelected = selectedForCurrent === index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelect(currentQuestion.id, index)}
                  className={`flex w-full items-start gap-3 rounded-lg border-2 p-3.5 text-left transition-colors ${
                    isSelected
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-brand-100 hover:border-brand-300'
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${
                      isSelected ? 'bg-brand-600 text-white' : 'bg-brand-50 text-ink-900/60'
                    }`}
                  >
                    {OPTION_LETTERS[index]}
                  </span>
                  <span className="flex-1 pt-0.5 text-[13px] text-ink-900">{option.text}</span>
                  {isSelected && (
                    <CheckCircle2Icon size={18} className="mt-0.5 shrink-0 text-brand-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigatie + inleveren */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeftIcon size={15} />
          <span className="hidden sm:inline">Vorige</span>
        </button>

        {currentIndex < questions.length - 1 ? (
          <button
            type="button"
            onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <span className="hidden sm:inline">Volgende</span>
            <ChevronRightIcon size={15} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <SendIcon size={15} />
            Inleveren
          </button>
        )}
      </div>

      {/* Vraag-navigatiepunten */}
      <div className="mt-6 flex flex-wrap justify-center gap-2 border-t border-brand-100 pt-5">
        {questions.map((q, i) => {
          const isAnswered = Boolean(answers[q.id]);
          const isCurrent = i === currentIndex;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => setCurrentIndex(i)}
              className={`h-8 w-8 rounded-full text-[12px] font-medium transition-colors ${
                isCurrent
                  ? 'bg-brand-600 text-white'
                  : isAnswered
                    ? 'border border-green-300 bg-green-50 text-green-700'
                    : 'bg-brand-50 text-ink-900/50 hover:bg-brand-100'
              }`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* Bevestiging inleveren */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <AlertTriangleIcon size={24} className="mt-0.5 shrink-0 text-mosterdgeel-500" />
              <div>
                <h3 className="text-[16px] font-bold text-ink-900">Examen inleveren?</h3>
                <p className="mt-1 text-[13px] text-ink-900/65">
                  Weet je zeker dat je het examen wilt inleveren? Daarna kun je niets meer wijzigen.
                </p>
                {unansweredCount > 0 && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-mosterdgeel-100 bg-mosterdgeel-10 p-3">
                    <AlertTriangleIcon size={15} className="shrink-0 text-mosterdgeel-600" />
                    <p className="text-[12px] text-mosterdgeel-600">
                      Je hebt nog {unansweredCount} onbeantwoorde{' '}
                      {unansweredCount === 1 ? 'vraag' : 'vragen'}.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="rounded-xl px-4 py-2 text-[13px] text-ink-900/60 hover:bg-brand-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
              >
                <SendIcon size={15} />
                Ja, inleveren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBar({
  label,
  value,
  barClass,
}: {
  label: string;
  value: number;
  barClass: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <span className="w-36 shrink-0 text-[13px] text-ink-900/70">{label}</span>
      <div className="h-3 flex-1 overflow-hidden rounded-full bg-brand-50">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${value}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-[13px] font-semibold text-ink-900">
        {value}%
      </span>
    </div>
  );
}
